import { GetPublicOrganizationProductResponse, GetPublicOrganizationResponse, PaymentTargetType, ProductPeriod, PurchaseStatus, PurchaseSummaryResponse } from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators';
import { delay, isDefined, show_confetti } from '../helpers/utils';
import { formatPrice, getProductById } from '../helpers/api/products-api';

import { getPublicOrganizations } from '../helpers/api/organization-api';
import { getQueryStringParameters } from '../helpers/urls';
import { get_transaction_status } from '../helpers/api/purchases-api';

@customElement('checkout-return-page')
export class CheckoutReturnPage extends LitElement {
    static amplitude_callsite: string = 'Checkout Return Page';

    @state()
    loading: boolean = true;

    @state()
    transactionInfo?: PurchaseSummaryResponse;

    @state()
    url_status: 'cancelled' | 'success' | 'receipt' | undefined;

    @state()
    pending_transaction_id: string | undefined;

    @state()
    purchase_type: PaymentTargetType | undefined;

    @state()
    entity_write_key: string | undefined;

    @state()
    topic_key: string | undefined;

    @state()
    topic_id: string | undefined;

    @state()
    challenge_id: string | undefined;

    @state()
    organization_details: GetPublicOrganizationResponse | undefined;

    @state()
    error?: string;

    @state()
    purchased_products: GetPublicOrganizationProductResponse[] = [];

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        // get parameters from url
        const queryString = getQueryStringParameters();
        this.url_status = queryString.get('return_status') as 'cancelled' | 'success' | 'receipt' | undefined;
        this.pending_transaction_id = queryString.get('pending_transaction_id');
        this.entity_write_key = queryString.get('created_key');
        this.topic_key = queryString.get('topic_key');
        this.topic_id = queryString.get('topic_id');
        this.challenge_id = queryString.get('challenge_id');
        this.purchase_type = queryString.get('purchase_type') as PaymentTargetType | undefined;

        await this.loadTransactionStatus(this.url_status, this.pending_transaction_id);
        if (isDefined(this.transactionInfo)) {
            this.organization_details = (await getPublicOrganizations('CheckoutReturnPage', [this.transactionInfo?.organization_id.$oid]))[0];
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private async loadTransactionStatus(url_status: 'cancelled' | 'success' | 'receipt' | undefined, pending_transaction_id: string | undefined) {
        if (!isDefined(pending_transaction_id) || url_status === undefined) {
            this.error = 'Missing pending transaction ID or return status.';
            this.loading = false;
            return;
        }

        if (url_status === 'cancelled') {
            this.error = 'The payment was cancelled.';
        }

        await this.pollTransactionStatus(pending_transaction_id, 5, url_status);
    }

    private async pollTransactionStatus(pending_transaction_id: string, maxRetries: number = 5, url_status: 'cancelled' | 'success' | 'receipt' | undefined) {
        let attempts = 0;

        while (attempts < maxRetries) {
            try {
                this.transactionInfo = await get_transaction_status(pending_transaction_id);

                // Check if transaction completed successfully
                if (this.transactionInfo?.status === PurchaseStatus.Completed) {
                    this.loading = false;
                    // Show confetti on success page load
                    if (this.url_status === 'success') {
                        try {
                            void show_confetti();
                        } catch (error) {
                            console.error('Failed to show confetti:', error);
                        }
                        return;
                    }
                    this.loading = false;
                    const purchasedProducts: GetPublicOrganizationProductResponse[] = [];
                    try {
                        if (isDefined(this.transactionInfo?.purchase_items)) {
                            const productIds = this.transactionInfo?.purchase_items.map(item => item.product_id.$oid);
                            for (const productId of productIds) {
                                const product = await getProductById(productId);
                                purchasedProducts.push(product);
                            }
                        }
                        this.purchased_products = purchasedProducts;
                    } catch (error) {
                        console.error('Failed to load purchased products:', error);
                    }
                    return;
                }

                // Check if transaction failed permanently
                if (this.transactionInfo.status === PurchaseStatus.Failed) {
                    this.error = 'Payment processing failed. Please try again or contact support.';
                    this.loading = false;
                    return;
                }

                if (url_status === 'cancelled') {
                    this.error = 'The payment was cancelled.';
                    this.loading = false;
                    return;
                }

                // If still pending, wait and retry
                if (this.transactionInfo?.status === PurchaseStatus.Pending && attempts < maxRetries - 1) {
                    await delay(2000); // Wait 2 seconds before retry
                    attempts++;
                } else if (this.transactionInfo?.status === PurchaseStatus.Pending) {
                    // Max retries reached and still pending
                    this.error = 'Payment is still being processed. Please refresh the page in a few moments.';
                    this.loading = false;
                    return;
                } else {
                    // Unknown status
                    console.warn('Unknown transaction status:', this.transactionInfo?.status);
                    this.loading = false;
                    return;
                }

            } catch (error) {
                console.error(`Failed to load transaction status (attempt ${attempts + 1}):`, error);

                if (attempts === maxRetries - 1) {
                    this.error = 'Unable to verify payment status. Please contact support if your payment was processed.';
                    this.loading = false;
                    return;
                }

                // Wait before retrying on error
                await delay(1000);
                attempts++;
            }
        }
    }

    private getPaymentMethodDisplay(): string {
        if (this.transactionInfo?.card_brand && this.transactionInfo?.card_last4) {
            const brand = this.transactionInfo.card_brand.charAt(0).toUpperCase() + this.transactionInfo.card_brand.slice(1);
            return `${brand} ending in ${this.transactionInfo?.card_last4}`;
        }
        return 'Card ending in ****';
    }

    private getSuccessMessage() {
        const type = this.purchase_type;
        switch (type) {
            case PaymentTargetType.ActivatedChallenge:
                return 'Your registration has been successfully created and is now live!';
            case PaymentTargetType.Form:
                return 'Your form has been successfully submitted!';
            case PaymentTargetType.Ballot:
                return 'Your ballot has been successfully submitted!';
            default:
                return 'Your purchase was successful!';
        }
    }

    private getNextStepButton() {
        const type = this.purchase_type;
        const topicKey: string = this.topic_key ?? '';
        const entityWriteKey: string = this.entity_write_key ?? '';
        let records;

        if (this.transactionInfo?.purchase_records) {
            records = this.transactionInfo.purchase_records;
        }

        switch (type) {
            case PaymentTargetType.ActivatedChallenge: {
                const activated_challenge_id = records && 'ActivatedChallenge' in records && records.ActivatedChallenge?.activated_challenge_id?.$oid ? records.ActivatedChallenge.activated_challenge_id.$oid : '';
                return html`
                    ${this.entity_write_key ? html`
                        <button
                            class="primary-button"
                            @click=${() => { window.location.href = `#challenge?activated_challenge_id=${activated_challenge_id}&activated_challenge_key=${entityWriteKey ?? ''}&topic_key=${topicKey}&topic_id=${this.topic_id ?? ''}`; }}>
                            Go to Your Unique Page
                        </button>
                    ` : html`
                         <button
                            class="primary-button"
                            @click=${() => { window.location.href = `${window.location.origin}/#registration?topic_id=${this.topic_id ?? ''}&topic_key=${this.topic_key ?? ''}&challenge_id=${this.challenge_id ?? ''}`; }}>
                            Back to Registration Page
                        </button>
                    `}
                `;
            }
            case PaymentTargetType.Form: {
                const point_id = records && 'Form' in records && records.Form?.point_id?.$oid ? records.Form.point_id.$oid : '';
                const topic_id = records && 'Form' in records && records.Form?.topic_id?.$oid ? records.Form.topic_id.$oid : this.topic_id ?? '';
                return html`
                    ${this.entity_write_key ? html`
                        <button
                            class="primary-button"
                            @click=${() => { window.location.href = `#point?point_id=${point_id}&topic_id=${topic_id}&point_key=${entityWriteKey}&topic_key=${topicKey}`; }}>
                            Go to Edit Form
                        </button>
                    ` : ''}
                    <button
                        class="primary-button"
                        @click=${() => { window.location.href = `#command-center?point_id=${point_id}&topic_id=${topic_id}&topic_key=${topicKey}`; }}>
                        Go to Map
                    </button>
                `;
            }
            case PaymentTargetType.Ballot: {
                const topic_id = records && 'Ballot' in records && records.Ballot?.topic_id?.$oid ? records.Ballot.topic_id.$oid : this.topic_id ?? '';
                return html`
                    <button
                        class="primary-button"
                        @click=${() => { window.location.href = `#command-center?topic_id=${topic_id}&topic_key=${topicKey}`; }}>
                        Back to Map
                    </button>
                    <button
                        class="secondary-button"
                        @click=${() => { window.location.href = `#ballot?topic_id=${topic_id}&topic_key=${topicKey}`; }}>
                        Back to Voting Page
                    </button>
                `;
            }
            case PaymentTargetType.Paywall: {
                const topic_id = records && 'Paywall' in records && records.Paywall?.topic_id?.$oid ? records.Paywall.topic_id.$oid : this.topic_id ?? '';
                return html`
                    <button
                        class="primary-button"
                        @click=${() => { window.location.href = `#command-center?topic_id=${topic_id}&topic_key=${topicKey}`; }}>
                        Go to Map
                    </button>
                    ${this.url_status === 'success' ? html`
                        <div>Hint: Sometimes you may need to refresh the map page to see your new access level or click "already purchased" then confirm your email.</div>
                    ` : ''}
                `;
            }
        }
    }

    private isSubscription(): boolean {
        const hasSubscriptionId = isDefined(this.transactionInfo?.subscription_id);
        const hasRecurringItem = this.transactionInfo?.purchase_items?.some(item => isDefined(item.recurring_interval)) ?? false;
        return hasSubscriptionId || hasRecurringItem;
    }

    private getRecurringIntervalText(): string {
        const recurringInterval = this.transactionInfo?.purchase_items?.find(item => isDefined(item.recurring_interval))?.recurring_interval;
        if (!isDefined(recurringInterval)) {
            return 'Recurring billing';
        }
        switch (recurringInterval) {
            case ProductPeriod.Weekly:
                return 'Billed weekly';
            case ProductPeriod.Yearly:
                return 'Billed yearly';
            default:
                return 'Billed monthly';
        }
    }

    render() {
        if (this.loading) {
            return html`
                <div class="container">
                    <loading-element loadingString="Loading payment details..."></loading-element>
                </div>
            `;
        }

        if (this.error) {
            return html`
                <div class="container">
                    <div class="error-card">
                        <h1>⚠️ Error</h1>
                        <p>${this.error}</p>
                        ${this.getNextStepButton()}
                    </div>
                </div>
            `;
        }

        return html`
            <div class="receipt-container">
                ${this.url_status === 'success' ? html`
                    <setting-card class="receipt-card" style="text-align:center;">
                        <p>${this.getSuccessMessage()}</p>
                    </setting-card>
                ` : ''}

                <setting-card class="actions-card">
                    ${this.getNextStepButton()}
                </setting-card>
                <setting-card class="receipt-card">
                    <div class="receipt-header">
                        <div class="success-indicator">
                            <span class="success-icon">✓</span>
                            <h1>Payment Receipt</h1>
                        </div>
                        <div class="organization-name">
                            <strong>${this.organization_details?.name ?? 'Proxi Maps Services'}</strong>
                        </div>
                        <div class="receipt-date">
                            ${new Date(this.transactionInfo?.updated_at ?? Date.now()).toLocaleDateString()} ${new Date(this.transactionInfo?.updated_at ?? Date.now()).toLocaleTimeString()}
                        </div>
                    </div>

                    <div class="receipt-details">
                        ${isDefined(this.transactionInfo?.invoice_id) ? html`
                            <div class="receipt-row">
                                <span class="label">Confirmation ID:</span>
                                <span class="value">${this.transactionInfo?.purchase_id?.$oid ?? 'N/A'}</span>
                            </div>
                        ` : ''}

                        <div class="receipt-row">
                            <span class="label">Date:</span>
                            <span class="value">${new Date(this.transactionInfo?.updated_at ?? Date.now()).toLocaleDateString()}</span>
                        </div>

                        <div class="receipt-row">
                            <span class="label">Amount:</span>
                            <span class="value amount">${formatPrice(this.transactionInfo?.total_price_cents ?? 0, this.transactionInfo?.currency ?? 'USD')}</span>
                        </div>

                        <div class="receipt-row">
                            <span class="label">Payment Method:</span>
                            <span class="value">${this.getPaymentMethodDisplay()}</span>
                        </div>

                        <div class="receipt-row">
                            <span class="label">Purchase Type:</span>
                            <span class="value">${this.purchase_type ?? 'Digital Purchase'}</span>
                        </div>

                        <div class="receipt-row">
                        ${this.purchased_products.length > 0 ? html`
                                <span class="label">Items</span>
                                <span class="value">
                                ${this.transactionInfo?.purchase_items?.map((item, index) => {
                                    const product = this.purchased_products.find(p => p._id.$oid === item.product_id.$oid);
                                    if (!product) return '';
                                    return html`<span>${product.name} x${item.quantity}: ${((item.price_cents ?? 0) / 100).toFixed(2)}</span><br>`;
                                }) ?? ''}
                                </span>
                        ` : ''}
                        </div>

                        <div class="receipt-row">
                            <span class="label">Email:</span>
                            <span class="value">${this.transactionInfo?.user_email ?? 'Unknown'}</span>
                        </div>

                        <div class="receipt-row status-row">
                            <span class="label">Status:</span>
                            <span class="value status-completed">${this.transactionInfo?.status ?? 'Completed'}</span>
                        </div>
                        ${this.isSubscription() ? html`
                            <div class="receipt-row">
                                <span class="label">Subscription ID:</span>
                                <span class="value">${this.transactionInfo?.subscription_id ?? 'N/A'}</span>
                            </div>
                            <div class="receipt-row">
                                <span class="label">Subscription Recurrence:</span>
                                <span class="value">${this.getRecurringIntervalText()}</span>
                            </div>
                        ` : ''}
                    </div>
                </setting-card>
                ${this.isSubscription() ? html`
                    <setting-card>
                        <div class="subscription-block">
                            ${isDefined(this.transactionInfo?.subscription_id) ? html`
                                <div class="subscription-notice">
                                    <p>This purchase is part of a subscription. View full subscription details including transaction history and management options.</p>
                                    <button
                                        class="view-subscription-button"
                                        @click=${() => { window.location.href = `#subscription-details?subscription_id=${this.transactionInfo?.subscription_id as string ?? ''}&organization_id=${this.transactionInfo?.organization_id.$oid ?? ''}`; }}>
                                        View Subscription Details
                                    </button>
                                </div>
                            ` : ''}
                        </div>
                    </setting-card>
                ` : ''}
                <setting-card class="actions-card">
                    <button class="print-button" @click=${() => window.print()}>
                        🖨️ Print Receipt
                    </button>
                </setting-card>
                <div class="receipt-footer">
                    <p>Thank you for your purchase!</p>
                    <p class="support-info">Questions? Contact support at ${this.transactionInfo?.support_email ?? 'help@proxi.co'}</p>
                </div>
            </div>
        `;
    }

    static styles = css`
        :host {
            width: 100%;
            min-height: 100vh;
            background: #f5f5f5;
            padding: 2rem 1rem;
            overflow-y: scroll;
        }

        .receipt-container {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            max-width: 600px;
            margin: 0 auto;
        }

        .receipt-card {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
        }

        .actions-card {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 1rem;
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 1rem;
        }

        .receipt-header {
            padding: 2rem 2rem 1rem 2rem;
            border-bottom: 2px solid #e0e0e0;
            text-align: center;
        }

        .success-indicator {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1rem;
            margin-bottom: 1rem;
        }

        .success-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: #10b981;
            color: white;
            font-size: 1.5rem;
            font-weight: bold;
            border-radius: 50%;
        }

        h1 {
            margin: 0;
            font-size: 1.8rem;
            color: #1f2937;
            font-weight: 600;
        }

        .organization-name {
            margin-bottom: 0.5rem;
            font-size: 1.2rem;
            color: #374151;
        }

        .receipt-date {
            color: #6b7280;
            font-size: 0.9rem;
        }

        .details-section {
            margin: 2rem 0;
            padding: 1.5rem;
            background: #f9fafb;
            border-radius: 8px;
        }

        .receipt-details {
            padding: 1.5rem 2rem;
        }

        .receipt-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 0;
            border-bottom: 1px solid #f0f0f0;
        }

        .receipt-row:last-child {
            border-bottom: none;
        }

        .status-row {
            border-top: 2px solid #e0e0e0;
            margin-top: 0.5rem;
            padding-top: 1rem;
            font-weight: 600;
        }

        .receipt-row .label {
            color: #6b7280;
            font-size: 0.95rem;
            font-weight: 500;
        }

        .receipt-row .value {
            color: #1f2937;
            font-weight: 500;
            text-align: right;
        }

        .value.amount {
            font-size: 1.1rem;
            font-weight: 700;
            color: #10b981;
        }

        .status-completed {
            color: #10b981 !important;
            text-transform: uppercase;
            font-weight: 600;
        }

        .receipt-footer {
            padding: 1.5rem 2rem;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            background: #f9fafb;
        }

        .receipt-footer p {
            margin: 0.5rem 0;
            color: #374151;
        }

        .support-info {
            font-size: 0.85rem;
            color: #6b7280;
        }

        .action-buttons {
            padding: 1.5rem;
            display: flex;
            gap: 1rem;
            justify-content: center;
        }        .primary-button {
            background: #667eea;
            color: white;
            border: none;
            padding: 1rem 2rem;
            font-size: 1.1rem;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            transition: background 0.2s;
        }

        .primary-button:hover {
            background: #5568d3;
        }

        .secondary-button {
            background: #6b7280;
            color: white;
            border: none;
            padding: 1rem 2rem;
            font-size: 1.1rem;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            transition: background 0.2s;
        }

        .secondary-button:hover {
            background: #4b5563;
        }

        .primary-button, .print-button {
            background: #667eea;
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            transition: background 0.2s;
            min-width: 120px;
        }

        .primary-button:hover, .print-button:hover {
            background: #5568d3;
        }

        .print-button {
            background: #6b7280;
        }

        .print-button:hover {
            background: #4b5563;
        }

        .error-card {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            padding: 2rem;
            text-align: center;
        }

                .error-card h1 {
            color: #dc2626;
            margin: 0 0 1rem 0;
        }

        .subscription-notice {
            margin-top: 1rem;
            padding: 1rem;
            background: #f0f9ff;
            border-radius: 6px;
            border: 1px solid #bae6fd;
        }

        .subscription-notice p {
            margin: 0 0 1rem 0;
            color: #0c4a6e;
            font-size: 0.95rem;
        }

        .view-subscription-button {
            background: #667eea;
            color: white;
            border: none;
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
            border-radius: 6px;
            cursor: pointer;
            font-weight: 600;
            transition: background 0.2s;
            width: 100%;
        }

        .view-subscription-button:hover {
            background: #5568d3;
        }

        .subscription-actions {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-top: 0.75rem;
        }

        .subscription-actions .secondary-button {
            align-self: flex-start;
            padding: 0.5rem 0.75rem;
        }

        /* Print styles for clean receipt printing */
        @media print {
            :host {
                background: white !important;
                padding: 0 !important;
            }

            .receipt-container {
                max-width: none;
                margin: 0;
                gap: 0;
            }

            .actions-card {
                display: none !important;
            }

            .receipt-card {
                border: none;
                box-shadow: none;
            }
        }

        @media (max-width: 640px) {
            :host {
                padding: 1rem;
            }

            .receipt-header {
                padding: 1.5rem 1.5rem 1rem 1.5rem;
            }

            .receipt-details {
                padding: 1rem 1.5rem;
            }

            h1 {
                font-size: 1.5rem;
            }

            .success-icon {
                width: 32px;
                height: 32px;
                font-size: 1.2rem;
            }

            .receipt-row {
                flex-direction: column;
                align-items: flex-start;
                gap: 0.25rem;
                padding: 0.5rem 0;
            }

            .receipt-row .value {
                font-size: 0.95rem;
            }

            .action-buttons {
                flex-direction: column;
                padding: 1rem;
            }

            .primary-button, .print-button {
                width: 100%;
            }
        }
    `;
}
